import { Box, Stack, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DragHintKey, DragHintText, DragReadoutCard } from '../DragReadoutCard';
import { usePlanEditor } from '../editor';

import { TimelineDropOverlay } from './TimelineDropOverlay';
import {
  GRID_DAY_MIN_PX,
  LEFT_COLUMN_WIDTH_PX,
  MONTH_BAND_HEIGHT_PX,
  TIMELINE_ROW_HEIGHT_PX,
} from './timelineGeometry';
import { TimelineHeader } from './TimelineHeader';
import { offsetToIso } from './timelineModel';
import { TimelineRow } from './TimelineRow';
import { TimelineZoomControl } from './TimelineZoomControl';
import { useBarDrag } from './useBarDrag';
import { useRowDrag } from './useRowDrag';
import { useTimelineModel } from './useTimelineWindow';
import { useTimelineZoom } from './useTimelineZoom';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MicroLabel } from '@/components/ui';
import { formatHours, formatShortDate } from '@/lib';
import { zIndex } from '@/theme';

export type PlanTimelineProps = {
  scrollElement: HTMLElement | null;
};

/**
 * The plan as a Gantt chart, editable in place.
 *
 * Scroll model, which drives the whole DOM shape: vertical scrolling stays with
 * the page's `PanelContent` (ADR 0013's single scroll owner), so this component
 * only owns a HORIZONTAL scroller for the time axis. That is also why the month
 * band lives outside that scroller — a `position: sticky; top: 0` inside an
 * `overflow-x: auto` box resolves against the box, not the page, so the band
 * would scroll away with the rows. Its horizontal offset is mirrored
 * imperatively instead, which costs no React render.
 */
export const PlanTimeline = ({ scrollElement }: PlanTimelineProps) => {
  const { t } = useTranslation(['pmPlanning', 'pmCommon']);
  const { flat, state, dispatch, rollup } = usePlanEditor();
  const model = useTimelineModel();

  const rows = flat.rows;
  const listRef = useRef<HTMLDivElement | null>(null);
  const bodyScrollRef = useRef<HTMLDivElement | null>(null);
  const bandTrackRef = useRef<HTMLDivElement | null>(null);

  // Zoom before drag: the drag hook divides by dayWidth, while the zoom hook
  // only needs the dragging id inside wheel handlers — so it takes a ref that
  // is mirrored right after `useBarDrag` returns.
  const draggingIdRef = useRef<string | null>(null);
  const { dayWidth, sliderValue, setSliderValue, zoomBy } = useTimelineZoom(
    bodyScrollRef,
    bandTrackRef,
    model.window.dayCount,
    draggingIdRef,
  );
  const barDrag = useBarDrag(model, dayWidth);
  const { draggingId, barPointerDown, trackClick } = barDrag;
  useEffect(() => {
    draggingIdRef.current = draggingId;
  }, [draggingId]);
  const rowDrag = useRowDrag(scrollElement);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => TIMELINE_ROW_HEIGHT_PX,
    overscan: 12,
    getItemKey: (index) => rows[index]!.id,
    /**
     * There is real content above this list (project header, banner, toolbar,
     * month band). Without the offset the virtualizer assumes the list starts at
     * scrollTop 0 and renders the wrong slice once the page is scrolled.
     */
    scrollMargin: listRef.current?.offsetTop ?? 0,
  });

  // Keep the selected (or newly created, title-editing) row in view when
  // arriving from the list view or the keyboard, matching PlanTree.
  const focusId = state.editing?.nodeId ?? state.activeId;
  useEffect(() => {
    if (!focusId) return;
    const index = flat.index.rowIndexById.get(focusId);
    if (index !== undefined) virtualizer.scrollToIndex(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scroll on focus change only
  }, [focusId]);

  const onBodyScroll = useCallback(() => {
    const track = bandTrackRef.current;
    const scroller = bodyScrollRef.current;
    if (track && scroller) track.style.transform = `translateX(${String(-scroller.scrollLeft)}px)`;
  }, []);

  const { window: timelineWindow } = model;
  // Rounded once here so the band track, the list box and every row agree on
  // the same integer width at fractional day widths.
  const trackWidth = Math.round(timelineWindow.dayCount * dayWidth);
  const totalWidth = LEFT_COLUMN_WIDTH_PX + trackWidth;
  // Below ~3px/day the 1px day lines merge into solid fill; draw weekly lines.
  const gridPeriod = dayWidth >= GRID_DAY_MIN_PX ? dayWidth : dayWidth * 7;

  return (
    <Box data-automation-id="plan-timeline" data-day-width={dayWidth}>
      {/* Month band: sticky to the PAGE scroller, so outside the horizontal one. */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: zIndex.stickyHeader,
          overflow: 'hidden',
          height: MONTH_BAND_HEIGHT_PX + 12,
          bgcolor: 'var(--bg-panel)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <Box ref={bandTrackRef} sx={{ display: 'flex', width: `${String(totalWidth)}px` }}>
          <Box
            sx={{
              width: LEFT_COLUMN_WIDTH_PX,
              flex: 'none',
              py: 1,
              px: 2,
              borderRight: '1px solid var(--border-divider)',
            }}
          >
            <MicroLabel>{t('pmCommon:term.Task')}</MicroLabel>
          </Box>
          <TimelineHeader window={timelineWindow} dayWidth={dayWidth} />
        </Box>
        {/* Anchored to the sticky band itself, outside the translated track,
            so it stays put under both scroll axes. */}
        <TimelineZoomControl
          sliderValue={sliderValue}
          onSliderChange={setSliderValue}
          onZoomBy={zoomBy}
        />
      </Box>

      <Box
        ref={bodyScrollRef}
        onScroll={onBodyScroll}
        sx={{
          // Horizontal only. The inner box is exactly as tall as the virtual
          // list, so there is no vertical overflow to hide.
          overflowX: 'auto',
          overflowY: 'hidden',
          overscrollBehaviorX: 'contain',
        }}
      >
        <Box
          ref={listRef}
          sx={{
            position: 'relative',
            height: `${String(virtualizer.getTotalSize())}px`,
            width: `${String(totalWidth)}px`,
            // One gradient paint for the whole grid rather than one per row.
            backgroundImage: `repeating-linear-gradient(to right, var(--border-divider) 0 1px, transparent 1px ${String(gridPeriod)}px)`,
            backgroundPosition: `${String(LEFT_COLUMN_WIDTH_PX)}px 0`,
          }}
        >
          {timelineWindow.todayOffset !== null && (
            <Box
              data-automation-id="timeline-today"
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                width: '1px',
                left: `${String(LEFT_COLUMN_WIDTH_PX + timelineWindow.todayOffset * dayWidth)}px`,
                bgcolor: 'var(--color-coral-80)',
                opacity: 0.5,
                pointerEvents: 'none',
                zIndex: zIndex.rowAffordance,
              }}
            />
          )}

          {virtualizer.getVirtualItems().map((item) => {
            const row = rows[item.index]!;
            const span = model.spanById.get(row.id) ?? null;
            const isParent = model.isParentById.get(row.id) ?? false;
            const nodeRollup = rollup.nodes[row.id];
            const startIso = span ? offsetToIso(timelineWindow, span.startOffset) : '';
            const endIso = span ? offsetToIso(timelineWindow, span.endOffset) : '';

            return (
              <Box
                key={item.key}
                data-index={item.index}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${String(item.start - virtualizer.options.scrollMargin)}px)`,
                }}
              >
                <TimelineRow
                  row={row}
                  span={span}
                  isParent={isParent}
                  dayCount={timelineWindow.dayCount}
                  dayWidth={dayWidth}
                  hoursLabel={
                    nodeRollup && nodeRollup.hours > 0
                      ? `${formatHours(nodeRollup.hours)} ${t('pmCommon:unit.HoursShort')}`
                      : '—'
                  }
                  childrenSpan={model.descendantSpanById.get(row.id) ?? null}
                  provisional={model.provisionalById.get(row.id) ?? false}
                  targetOffset={model.targetOffsetById.get(row.id) ?? null}
                  barStartIso={startIso}
                  barEndIso={endIso}
                  barLabel={
                    span
                      ? `${formatShortDate(startIso)} → ${formatShortDate(endIso)}${
                          model.provisionalById.get(row.id)
                            ? ` · ${t('timeline.BarProvisionalHint')}`
                            : ''
                        }${isParent ? ` · ${t('timeline.BarGroupHint')}` : ''}`
                      : ''
                  }
                  active={state.activeId === row.id}
                  dragging={draggingId === row.id}
                  inDraggedBlock={
                    rowDrag.drag !== null &&
                    item.index >= rowDrag.drag.fromIndex &&
                    item.index <= rowDrag.drag.toIndex
                  }
                  justMoved={rowDrag.justMovedId === row.id}
                  onSelect={() => {
                    if (state.activeId !== row.id) dispatch({ type: 'SET_ACTIVE', id: row.id });
                  }}
                  onNamePointerDown={rowDrag.namePointerDown(row.id)}
                  onBarPointerDown={barPointerDown(row.id)}
                  onTrackClick={trackClick(row.id)}
                />
              </Box>
            );
          })}

          {rowDrag.drag && (
            <TimelineDropOverlay
              drag={rowDrag.drag}
              feedback={rowDrag.feedback}
              totalWidth={totalWidth}
              dayWidth={dayWidth}
              parentSpan={
                rowDrag.feedback?.outcome.kind === 'move' && rowDrag.feedback.outcome.parentId
                  ? (model.spanById.get(rowDrag.feedback.outcome.parentId) ?? null)
                  : null
              }
              dragSpan={model.spanById.get(rowDrag.drag.nodeId) ?? null}
            />
          )}
        </Box>
      </Box>

      {rowDrag.drag && (
        <DragReadoutCard
          cardRef={rowDrag.cardRef}
          name={flat.index.nodeById.get(rowDrag.drag.nodeId)?.name ?? ''}
          descendantCount={flat.index.descendantCountById.get(rowDrag.drag.nodeId) ?? 0}
          outcome={rowDrag.feedback?.outcome ?? null}
          idPrefix="timeline"
          levelHint={
            <>
              <DragHintKey>←</DragHintKey>
              <DragHintKey>→</DragHintKey>
              <DragHintText>{t('keyboardHints.ChangeLevel')}</DragHintText>
            </>
          }
        />
      )}

      {/*
        The same card, for the other drag. A bar drag had no feedback at all
        until now — the dates only existed in the bar's hover title and in the
        side panel, neither of which is where the pointer is.
      */}
      {barDrag.draggingId !== null && barDrag.readout && (
        <DragReadoutCard
          cardRef={barDrag.cardRef}
          name={flat.index.nodeById.get(barDrag.draggingId)?.name ?? ''}
          descendantCount={flat.index.descendantCountById.get(barDrag.draggingId) ?? 0}
          schedule={barDrag.readout}
          idPrefix="timeline"
          cardAutomationId="timeline-schedule-card"
        />
      )}

      <Stack
        direction="row"
        data-automation-id="timeline-footer"
        sx={{ alignItems: 'center', gap: 0.75, px: 2, py: 1.25 }}
      >
        <MaterialSymbol name="info" size={14} color="var(--fg-3)" />
        <Typography variant="label" sx={{ color: 'var(--fg-3)' }}>
          {model.unscheduledLeafCount > 0
            ? t('timeline.Unscheduled', { count: model.unscheduledLeafCount })
            : model.provisionalLeafCount > 0
              ? t('timeline.Provisional', { count: model.provisionalLeafCount })
              : t('timeline.AllScheduled')}
          {` · ${t('timeline.FooterHint')}`}
        </Typography>
      </Stack>
    </Box>
  );
};
